import {useAtom, useSetAtom} from 'jotai';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {LoadingBar} from '@/components/LoadingBar';
import {Typography} from '@/components/Typography';
import {AnalysisItem, KodataNearbyStoreItem} from '@/map/@types';
import {useKodataNearbyStoresByPoint} from '@/map/api';
import {internalToastAtom, selectedNearbyStoreItemCardAtom} from '@/map/atom';
import {NearbyStoreMarkers} from '@/map/components/MobileMaps/MarkerCluster/Stores';
import {convertToCoordFromPointCoords, getOverlapedMarkers} from '@/map/utils';
import {ColumnFlexBox} from '@/styled-components';
import {palette} from '@/styles';
import {ErrorBox} from '../ErrorBox';
import {NoData} from '../NoData';
import {ReportContainer} from '../StyledComponents';
import NewBadgeIcon from './new-badge.svg';

interface IProps {
  map?: naver.maps.Map;
  item: AnalysisItem;
  activeCategory: string | null;
  totalCount?: number;
}

type ItemWithPosition = KodataNearbyStoreItem & {position: naver.maps.Coord};

const DEFAULT_LIMIT = 100;

export const CategoryStores = ({
  map,
  activeCategory,
  item,
  totalCount,
}: IProps) => {
  const dataLimit = activeCategory ? undefined : DEFAULT_LIMIT;

  const {data, error, isPending} = useKodataNearbyStoresByPoint(
    item,
    activeCategory ? [activeCategory] : [],
    dataLimit
  );
  const [selectedItem, setSelectedItem] = useAtom(
    selectedNearbyStoreItemCardAtom
  );
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const selectedCardIndex = useRef<number>(0);

  const setInternalToast = useSetAtom(internalToastAtom);

  const markers = useMemo<
    Array<ItemWithPosition & {overlapped: ItemWithPosition[]}>
  >(() => {
    const res = data && getOverlapedMarkers(data, 'branchName');

    return res;
  }, [data]);

  const onItemClick = (item: KodataNearbyStoreItem) => {
    if (!markers) {
      return;
    }

    const index = markers.findIndex((i) =>
      convertToCoordFromPointCoords(item.location.coordinates)?.equals(
        i.position
      )
    );

    if (index === -1) {
      return;
    }

    setSelectedIndex(index);

    const mainItem = markers[index];

    if (
      mainItem &&
      mainItem.branchName !== item.branchName &&
      mainItem.overlapped?.length
    ) {
      const cardIndex = mainItem.overlapped.findIndex(
        (i) => i.branchName === item.branchName
      );
      selectedCardIndex.current = cardIndex + 1;
    }
  };

  const onMarkerClick = useCallback(
    (idx: number) => {
      setSelectedIndex(selectedIndex === idx ? -1 : idx);
    },
    [selectedIndex]
  );

  useEffect(() => {
    if (selectedIndex >= 0 && markers) {
      const selected = markers[selectedIndex];

      if (selected) {
        const items = [selected, ...selected.overlapped];
        setSelectedItem({items, index: selectedCardIndex.current || 0});
      }
    } else {
      setSelectedItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  useEffect(() => {
    if (!selectedItem) {
      setSelectedIndex(-1);
      selectedCardIndex.current = 0;
    }
  }, [selectedItem]);

  useEffect(() => {
    return () => {
      setSelectedItem(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      dataLimit &&
      data?.length &&
      data.length >= DEFAULT_LIMIT &&
      totalCount &&
      totalCount > data.length
    ) {
      setInternalToast('매장이 많아 일부만 표시돼요. 반경을 좁혀보세요!');
    } else {
      setInternalToast(null);
    }

    return () => {
      setInternalToast(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLimit, data?.length, totalCount]);

  if (!activeCategory?.length) {
    return (
      <NearbyStoreMarkers
        map={map}
        items={markers}
        selectedItemsIndex={selectedIndex}
        onClick={onMarkerClick}
      />
    );
  }

  if (error) {
    return (
      <ReportContainer>
        <ErrorBox />
      </ReportContainer>
    );
  } else if (isPending) {
    return (
      <ReportContainer>
        <LoadingBar color={palette.textSecondary} />
      </ReportContainer>
    );
  }

  if (!data?.length) {
    return (
      <ReportContainer>
        <NoData text="분석 지역 내 주변 매장 정보가 없습니다." />
      </ReportContainer>
    );
  }

  return (
    <>
      <NearbyStoreMarkers
        map={map}
        items={markers}
        selectedItemsIndex={selectedIndex}
        onClick={onMarkerClick}
      />
      <ReportContainer>
        <List>
          {data?.map((i) => (
            <Item
              key={i.no + i.branchName}
              item={i}
              onClick={() => onItemClick(i)}
            />
          ))}
        </List>
      </ReportContainer>
    </>
  );
};

const Item = ({item, onClick}: {item: KodataNearbyStoreItem; onClick: any}) => {
  return (
    <ItemRoot onClick={onClick}>
      <ColumnFlexBox gap={4}>
        {item.newStore && (
          <img src={NewBadgeIcon} alt="NEW" width={22} height={16} />
        )}
        <Typography variant="medium16">{item.branchName}</Typography>
      </ColumnFlexBox>
    </ItemRoot>
  );
};

const List = styled.div``;

const ItemRoot = styled.div`
  padding-bottom: 16px;
  & + & {
    padding-top: 16px;
    border-top: 1px solid ${palette.gray100};
  }
  display: flex;
  gap: 16px;
`;
